import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { downloadFile } from './ui';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import AdvancesPanel from './AdvancesPanel';
import { useCanManageFinance } from '../../../hooks/useFinanceRole';

const METHOD = { bank_transfer: 'Bank transfer', upi: 'UPI', cash: 'Cash', cheque: 'Cheque', card: 'Card', other: 'Other' };
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export default function ReceiptsView({ refreshKey, onChanged }) {
  const canFinance = useCanManageFinance();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/receipts').then((res) => setReceipts(res.data.receipts || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const voidReceipt = async (r) => {
    if (!(await alertService.confirm('Void receipt?', 'The invoices it settled become unpaid again and the ledger entry is reversed.', 'danger', 'Void'))) return;
    try {
      await api.post(`/crm/receipts/${r._id}/void`);
      alertService.success('Receipt voided');
      load(); onChanged?.();
    } catch (e) {
      alertService.error('Could not void', e.response?.data?.message || '');
    }
  };

  return (
    <>
    <AdvancesPanel refreshKey={refreshKey} onChanged={() => { load(); onChanged?.(); }} />
    <div className="bg-white border border-[#D8DDE6] rounded shadow-sm">
      <div className="flex items-center justify-between p-3 border-b border-[#D8DDE6]">
        <p className="text-[13px] text-[#706E6B]">Money received from customers, and the invoices each receipt settled.</p>
        <button type="button" onClick={load} aria-label="Refresh receipts" className="h-8 w-8 flex items-center justify-center rounded border border-[#D8DDE6]"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] sm:min-w-[47.5rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
            <th className="text-left font-semibold px-4 py-2.5">Date</th><th className="text-left font-semibold px-4 py-2.5">Customer</th>
            <th className="text-left font-semibold px-4 py-2.5">Method</th><th className="text-left font-semibold px-4 py-2.5">Invoices settled</th>
            <th className="text-right font-semibold px-4 py-2.5">Amount</th><th className="px-4 py-2.5" />
          </tr></thead>
          <tbody>
            {receipts.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#706E6B]">{loading ? 'Loading…' : 'No receipts recorded yet.'}</td></tr>}
            {receipts.map((r) => (
              <tr key={r._id} className={cn('border-b border-[#F3F2F2]', r.status === 'void' && 'opacity-50')}>
                <td className="px-4 py-2.5">{fmtDate(r.date)}</td>
                <td className="px-4 py-2.5">{r.accountId?.name || r.contactId?.name || '—'}</td>
                <td className="px-4 py-2.5">{METHOD[r.method] || r.method}{r.reference && <span className="block text-[11px] text-[#706E6B]">{r.reference}</span>}</td>
                <td className="px-4 py-2.5 text-[12px]">{r.allocations.map((a) => `${a.invoiceId?.invoiceNumber || '—'} (${formatCurrency(a.amount, r.currency)})`).join(', ')}</td>
                <td className={cn('px-4 py-2.5 text-right tabular-nums font-semibold', r.status === 'void' && 'line-through')}>{formatCurrency(r.amount, r.currency)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button type="button" onClick={() => downloadFile(`/crm/receipts/${r._id}/pdf`, `Receipt-${String(r._id).slice(-8).toUpperCase()}.pdf`)} className="text-[12px] font-semibold text-[#0176D3] mr-3">PDF</button>
                  {r.status === 'void'
                  ? <span className="text-[11px] text-[#706E6B]">Void</span>
                  : canFinance && <button type="button" onClick={() => voidReceipt(r)} className="text-[12px] font-semibold text-[#BA0517]">Void</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}
